/**
 * Build a `BetLineContext` for a collapsed value ladder (a player+stat's
 * 1+/2+/3+, a team's runs), so the right-side order panel gets the SAME
 * threshold strip the table row has — you can switch rungs from the order
 * entry itself, exactly like Spread/Total (Dan, 2026-07-24). Without this the
 * panel is a dead end: the row scrolls, the ticket doesn't.
 *
 * Mirrors buildBetLineContext (spread/total) — same shape, same panel
 * machinery. As there, panel-side chip clicks re-synthesize the panel's own
 * market via `getGameForIdx` and do NOT move the table's selection.
 */

import type { ReactNode } from 'react';
import type { LadderTableRow, LadderTarget } from './ladderRows';
import type { BetLineContext } from '../ValueDashboard/GameDetailPanel';
import type { GameRow, MarketSide } from '../ValueDashboard/types';

/** "Tyler Freeman (COL) 2+ Hits" → the "2+" in green, matching the
 *  spread/total titles, which highlight the line magnitude. Falls back to the
 *  plain label if the rung token isn't in the name (a label format changed —
 *  render it truthfully rather than guessing where the number goes). */
function highlightRung(fullLabel: string, rungLabel: string): ReactNode {
  const at = fullLabel.indexOf(rungLabel);
  if (at < 0) return <span>{fullLabel}</span>;
  return (
    <span>
      {fullLabel.slice(0, at)}
      <span className="text-positive">{rungLabel}</span>
      {fullLabel.slice(at + rungLabel.length)}
    </span>
  );
}

/**
 * @param row       the collapsed ladder row (must carry `chips`; a singleton
 *                  ladder is an ordinary bet and needs no context)
 * @param rungs     that ladder's chip-id → target map (ladderRows' rungTargets)
 * @param onSelect  push a panel-side selection back to the table. Passed for
 *                  completeness; the panel deliberately doesn't call it.
 */
export function buildLadderBetLineContext(
  row: LadderTableRow,
  rungs: Map<string, LadderTarget> | undefined,
  onSelect: (chipKey: string) => void,
): BetLineContext | null {
  const chips = row.chips;
  if (!chips || chips.length === 0 || row.selectedIdx == null) return null;

  const gameForIdx = (idx: number): GameRow | null =>
    rungs?.get(chips[idx]?.key ?? '')?.synth ?? null;

  return {
    // The rung's OWN full name ("… 2+ Hits"), not the row's threshold-free
    // title: the ticket has to say which threshold you're about to trade.
    titleFor: (_side: MarketSide, idx: number): ReactNode => {
      const synth = gameForIdx(idx);
      const label = synth?.awayName ?? row.displaySynth.awayName;
      return highlightRung(label, chips[idx]?.label ?? '');
    },
    chips,
    selectedIdx: row.selectedIdx,
    onSelectIdx: (idx) => {
      const key = chips[idx]?.key;
      if (key) onSelect(key);
    },
    getGameForIdx: gameForIdx,
  };
}
